import { Box, Divider, Group, Popover, Stack, Text } from '@mantine/core';
import { tKeys, useTranslation } from 'locales';
import { Fragment } from 'react';
import type { Sync } from 'shared/hooks/use-load-syncs';

import { useLastSyncLabel } from '../hooks/use-last-sync-label';
import { useSyncStatusLabel } from '../hooks/use-sync-status-label';
import { useSyncTitle } from '../hooks/use-sync-title';
import { SyncDot } from './SyncDot';
import { SyncSettingsLink } from './SyncPill';
import { SyncSummaryPill } from './SyncSummaryPill';

type SyncPopoverProps = {
  syncs: Sync[];
};

export const SyncPopover = ({ syncs }: SyncPopoverProps) => {
  const { t } = useTranslation();
  const syncStatusLabel = useSyncStatusLabel();
  const lastSyncLabel = useLastSyncLabel();
  const syncTitle = useSyncTitle();

  return (
    <Popover withArrow arrowSize={8} position="top" radius={8} shadow="md" width={280}>
      <Popover.Target>
        <Box style={{ cursor: 'pointer' }}>
          <SyncSummaryPill syncs={syncs} />
        </Box>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="space3">
          <Text fz="xs" fw={700} c="gray.6" tt="uppercase" style={{ letterSpacing: '0.06em' }}>
            {t(tKeys.NAV.SYNC.TITLE)}
          </Text>
          {syncs.map((sync, index) => {
            const isMailbox = sync.kind === 'email';
            const lastSync = lastSyncLabel(sync);

            return (
              <Fragment key={`${sync.kind}-${sync.integration}`}>
                {index > 0 && <Divider color="gray.2" />}
                <SyncSettingsLink integrationKey={sync.integrationKey}>
                  <Stack gap={2}>
                    <Group justify="space-between" gap="space3" wrap="nowrap">
                      <Group gap="space2" wrap="nowrap" style={{ minWidth: 0 }}>
                        <SyncDot state={sync.state} />
                        <Text fw={600} fz="sm" truncate>
                          {syncTitle(sync)}
                        </Text>
                      </Group>
                      <Text fz="xs" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                        {syncStatusLabel(sync)}
                      </Text>
                    </Group>
                    {isMailbox ? (
                      <Group gap={4} wrap="nowrap" pl={26}>
                        <Text fz="xs" c="gray.6" truncate style={{ minWidth: 0 }}>
                          {sync.integration}
                        </Text>
                        {lastSync !== null && (
                          <>
                            <Text fz="xs" c="gray.6">
                              ·
                            </Text>
                            <Text
                              fz="xs"
                              c="gray.6"
                              style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                            >
                              {lastSync}
                            </Text>
                          </>
                        )}
                      </Group>
                    ) : (
                      lastSync !== null && (
                        <Text fz="xs" c="gray.6" pl={26}>
                          {lastSync}
                        </Text>
                      )
                    )}
                  </Stack>
                </SyncSettingsLink>
              </Fragment>
            );
          })}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
